import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TurndownService from 'turndown';
import { marked } from 'marked';
import './descriptionEditor.css';

const turndown = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
});
// Keep underline tags (<u>) as raw HTML in markdown since standard Markdown has no underline
turndown.keep(['u']);

const DescriptionEditor = ({ markdown, onMarkdownChange, allowHeading }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
      Underline,
      Placeholder.configure({
        placeholder: 'Enter a brief description. Select text to format.',
      }),
    ],
    content: marked.parse(markdown || ''),
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      const md = turndown.turndown(html);
      onMarkdownChange(md);
    },
  });

  const [isLinkInputOpen, setIsLinkInputOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  useEffect(() => {
    if (editor && editor.isEditable) {
      const currentMd = editor.getHTML() ? turndown.turndown(editor.getHTML()) : '';
      if ((markdown || '') !== (currentMd || '')) {
        editor.commands.setContent(marked.parse(markdown || ''), false);
      }
    }
  }, [markdown, editor]);

  // Close link input when selection changes, except while typing in the URL field
  useEffect(() => {
    if (!editor) {
      return undefined;
    }

    const handleSelection = () => {
      const active = document.activeElement;
      if (active && active.classList && active.classList.contains('tiptap-link-input-field')) {
        return;
      }
      setIsLinkInputOpen(false);
    };

    editor.on('selectionUpdate', handleSelection);
    return () => {
      editor.off('selectionUpdate', handleSelection);
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  const applyLinkFromInput = () => {
    const url = linkUrl.trim();
    if (url === '') {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .unsetLink()
        .run();
    }
    else {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();
    }
    setIsLinkInputOpen(false);
  };

  // reserved for future use to prevent bubble menu focus loss when needed

  return (
    <div>
      <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }}>
        <div className="tiptap-bubble" role="group" aria-label="Inline formatting">
          {!isLinkInputOpen && (
            <div className="btn-group btn-group-sm">
              <button type="button" className="btn btn-default" onMouseDown={e => e.preventDefault()} onClick={() => editor.chain().focus().toggleBold().run()}>
                <strong>B</strong>
              </button>
              <button type="button" className="btn btn-default" onMouseDown={e => e.preventDefault()} onClick={() => editor.chain().focus().toggleItalic().run()}>
                <em>I</em>
              </button>
              <button type="button" className="btn btn-default" onMouseDown={e => e.preventDefault()} onClick={() => editor.chain().focus().toggleUnderline().run()}>
                <u>U</u>
              </button>
              {allowHeading && (
                <button
                  type="button"
                  className="btn btn-default"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                >
                  Title
                </button>
              )}
              <button type="button" className="btn btn-default" onMouseDown={e => e.preventDefault()} onClick={() => editor.chain().focus().toggleBulletList().run()}>
                • List
              </button>
              <button type="button" className="btn btn-default" onMouseDown={e => e.preventDefault()} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                1. List
              </button>
              <button
                type="button"
                className="btn btn-default"
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  const previousUrl = editor.getAttributes('link').href || 'https://';
                  setLinkUrl(previousUrl);
                  setIsLinkInputOpen(true);
                }}
              >
                Link
              </button>
            </div>
          )}
          {isLinkInputOpen && (
            <div className="tiptap-link-input">
              <input
                type="text"
                className="form-control input-sm tiptap-link-input-field"
                placeholder="https://example.com"
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { applyLinkFromInput(); } if (e.key === 'Escape') { setIsLinkInputOpen(false); } }}
              />
              <button
                type="button"
                className="btn btn-primary btn-icon"
                title="Apply"
                onMouseDown={e => e.preventDefault()}
                onClick={applyLinkFromInput}
              >
                ✓
              </button>
              <button
                type="button"
                className="btn btn-default btn-icon"
                title="Unset link"
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .extendMarkRange('link')
                    .unsetLink()
                    .run();
                  setIsLinkInputOpen(false);
                }}
              >
                ✕
              </button>
            </div>
          )}
        </div>
      </BubbleMenu>
      <div className="description-editor-container form-control">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

DescriptionEditor.defaultProps = {
  markdown: '',
  allowHeading: false,
};

DescriptionEditor.propTypes = {
  markdown: PropTypes.string,
  onMarkdownChange: PropTypes.func.isRequired,
  allowHeading: PropTypes.bool,
};

export default DescriptionEditor;
